import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import alertaService from '../../services/Alerta/AlertaService';
import { TagTipo, nivelEvento, COLOR_NIVEL } from './alertaVisual';

/**
 * Tablero de alertas.
 *
 * Muestra dos cosas que NO son lo mismo, y por eso estan separadas en la
 * pantalla:
 *
 *   - "Lo que te llego": las alertas entregadas a este usuario, con cuantas
 *     quedan sin leer y sin revisar. Sale de alerta_notificacion.
 *   - "Lo que podes ver": el volumen dentro de su alcance, por dia, por tipo y
 *     por transportista. Sale de monitoreo_det.
 *
 * Mezclarlos es el error clasico de estos tableros: el operador termina
 * creyendo que le llegaron 17.000 alertas cuando le llegaron once.
 *
 * Los graficos son SVG y divs, sin libreria: son barras: una dependencia de
 * 300 kB para dibujar rectangulos no se justifica.
 */

const RANGOS = [
    { label: 'Hoy', dias: 1 },
    { label: '7 días', dias: 7 },
    { label: '30 días', dias: 30 },
    { label: '90 días', dias: 90 }
];

const COLOR_TIPO = {
    CANDADO: 'var(--red-500)',
    PARADA: 'var(--orange-500)',
    BITACORA: 'var(--primary-color)',
    SERVICIO_VIP: 'var(--green-500)'
};

const TableroAlertas = () => {

    const toast = useRef(null);
    // Arranca en HOY: el tablero se abre para ver como viene el dia, no el mes.
    const [dias, setDias] = useState(1);
    const [datos, setDatos] = useState(null);
    const [cargando, setCargando] = useState(false);
    // El corte por transportista llega aparte: es el unico bloque caro.
    const [transp, setTransp] = useState(null);
    const [cargandoTransp, setCargandoTransp] = useState(false);

    const cargar = useCallback(async (d) => {
        setCargando(true);
        try {
            const r = await alertaService.tablero(d);
            setDatos(r || null);
        } catch (e) {
            setDatos(null);
            toast.current?.show({
                severity: 'error', summary: 'No se pudo cargar el tablero',
                detail: 'Reintentá en unos segundos.', life: 4000
            });
        } finally {
            setCargando(false);
        }
    }, []);

    const cargarTransp = useCallback(async (d) => {
        setCargandoTransp(true);
        setTransp(null);
        try {
            const r = await alertaService.tableroTransportistas(d);
            setTransp(r || null);
        } catch (e) {
            setTransp(null);
        } finally {
            setCargandoTransp(false);
        }
    }, []);

    // Las dos en paralelo: el tablero no espera al bloque lento.
    useEffect(() => { cargar(dias); cargarTransp(dias); }, [cargar, cargarTransp, dias]);

    const porDia = useMemo(() => (datos?.porDia || []), [datos]);
    const porTipo = useMemo(() => (datos?.porTipo || []), [datos]);
    const porEvento = useMemo(() => (datos?.porEvento || []), [datos]);
    const porTransp = useMemo(() => (transp?.porTransportista || []), [transp]);
    const kpis = datos?.kpis || {};

    const maxDia = useMemo(
        () => porDia.reduce((a, x) => Math.max(a, x.n), 0), [porDia]);
    const totalTipo = useMemo(
        () => porTipo.reduce((a, x) => a + x.n, 0), [porTipo]);
    const maxEvento = useMemo(
        () => porEvento.reduce((a, x) => Math.max(a, x.n), 0), [porEvento]);
    const maxTransp = useMemo(
        () => porTransp.reduce((a, x) => Math.max(a, x.n), 0), [porTransp]);

    const total = useMemo(() => porDia.reduce((a, x) => a + x.n, 0), [porDia]);
    const promedio = useMemo(() => {
        const conDatos = porDia.filter((x) => x.n > 0).length;
        return conDatos ? Math.round(total / conDatos) : 0;
    }, [porDia, total]);

    const nro = (n) => (n == null ? '—' : n.toLocaleString('es-EC'));

    // Un numero en un tablero que no lleva a las filas que lo forman obliga a
    // rehacer el filtro a mano en otra pantalla. Cada barra abre la consulta ya
    // filtrada por lo que se clickeo.
    const iso = (d) => d.toISOString().slice(0, 10);
    // Mismo rango que cuenta el tablero: dias de calendario. "Hoy" es hoy, y
    // N dias son hoy mas los N-1 anteriores. El backend usa exactamente este
    // corte, asi que el numero de la barra y el de la consulta coinciden.
    const rango = () => {
        const h = new Date();
        const d = new Date();
        d.setDate(d.getDate() - (dias - 1));
        return { desde: iso(d), hasta: iso(h) };
    };
    const irAConsulta = (extra) => {
        const q = new URLSearchParams({ ...rango(), ...extra });
        window.location.hash = `#/consulta-alertas?${q.toString()}`;
    };
    // Accesible: lo clicable tambien se alcanza con teclado.
    const clicable = (onClick, titulo) => ({
        onClick,
        onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } },
        role: 'button',
        tabIndex: 0,
        title: titulo,
        style: { cursor: 'pointer' }
    });

    /** dd/mm a partir de aaaa-mm-dd, sin pasar por Date para no correr el huso. */
    const diaCorto = (iso) => (iso || '').slice(8, 10) + '/' + (iso || '').slice(5, 7);

    // Las tarjetas de arriba son "lo que te llego", que vive en Mis Alertas, no
    // en la consulta: son notificaciones de esta persona, no el volumen del
    // alcance. Por eso llevan a otra pantalla que las barras de abajo.
    const irAMisAlertas = (soloPendientes) => {
        window.location.hash = soloPendientes
            ? '#/mis-alertas?soloPendientes=1' : '#/mis-alertas';
    };

    const tarjetaKpi = (titulo, valor, pie, color, onClick) => (
        <div className="col-6 md:col-3">
            <div className={`card mb-0 h-full${onClick ? ' barra-clic' : ''}`}
                 {...(onClick ? clicable(onClick, `${titulo}: ver en Mis alertas`) : {})}
                 style={color ? { borderLeft: `3px solid ${color}` } : {}}>
                <div className="text-xs uppercase text-color-secondary">{titulo}</div>
                <div className="text-3xl font-bold mt-1">{valor}</div>
                <div className="text-xs text-color-secondary">{pie}</div>
            </div>
        </div>
    );

    return (
        <div className="grid">
            <Toast ref={toast} />
            <style>{`
                .barra-clic { border-radius: 4px; transition: background .15s ease; }
                .barra-clic:hover { background: var(--surface-100); }
                .barra-clic:hover > div > div { filter: brightness(1.12); }
                .barra-clic:focus-visible { outline: 2px solid var(--primary-color);
                                            outline-offset: 2px; }
            `}</style>

            <div className="col-12">
                <div className="flex flex-wrap justify-content-between align-items-center gap-3">
                    <span className="text-xl font-bold">Tablero de alertas</span>
                    <div className="flex align-items-center gap-2 flex-wrap">
                        {RANGOS.map((r) => (
                            <Button key={r.dias} label={r.label}
                                    className={`p-button-sm ${dias === r.dias ? '' : 'p-button-outlined'}`}
                                    onClick={() => setDias(r.dias)} />
                        ))}
                        <Button icon="pi pi-refresh" className="p-button-text"
                                tooltip="Actualizar" onClick={() => cargar(dias)} />
                    </div>
                </div>
            </div>

            {datos?.sinAlcance && (
                <div className="col-12">
                    <Message severity="warn" className="w-full"
                             text="No tenés alertas asignadas todavía, así que el tablero sale vacío.
                                   Pedí que te den alcance en Suscripciones de alertas." />
                </div>
            )}

            {/* Lo primero que se ve si hay algo que hacer, como en el prototipo:
                lo accionable antes que el volumen. */}
            {kpis.porRevisar > 0 && (
                <div className="col-12">
                    <div className="card mb-0" style={{ borderLeft: '3px solid var(--orange-500)' }}>
                        <div className="font-bold">
                            <i className="pi pi-exclamation-triangle mr-2 text-orange-500" />
                            Tenés {nro(kpis.porRevisar)}{' '}
                            {kpis.porRevisar === 1 ? 'alerta' : 'alertas'} por revisar
                        </div>
                        <div className="text-sm text-color-secondary mt-1">
                            Tu suscripción exige confirmación. Mientras no marques revisado,
                            queda registrado como pendiente a tu nombre.
                        </div>
                    </div>
                </div>
            )}

            {/* Lo que le llego a esta persona. Numeros chicos y accionables. */}
            <div className="col-12">
                <div className="text-xs uppercase text-color-secondary mb-2">
                    Lo que te llegó {cargando && <span className="ml-2">cargando…</span>}
                </div>
                <div className="grid">
                    {tarjetaKpi('Recibidas', nro(kpis.recibidas),
                        dias === 1 ? 'hoy' : `en ${dias} días`,
                        'var(--primary-color)', () => irAMisAlertas(false))}
                    {tarjetaKpi('Sin leer', nro(kpis.sinLeer), 'esperan tu vista',
                        kpis.sinLeer > 0 ? 'var(--primary-color)' : null,
                        () => irAMisAlertas(false))}
                    {tarjetaKpi('Por revisar', nro(kpis.porRevisar), 'requieren tu confirmación',
                        kpis.porRevisar > 0 ? 'var(--orange-500)' : null,
                        () => irAMisAlertas(true))}
                    {/* Por respaldo no lleva a ningun lado: son alertas que NO le
                        llegaron a nadie, asi que no estan en Mis alertas de nadie. */}
                    {tarjetaKpi('Por respaldo', nro(kpis.porRespaldo), 'nadie estaba suscripto',
                        kpis.porRespaldo > 0 ? 'var(--red-500)' : null)}
                </div>
            </div>

            {/* Lo que puede ver. Volumen, para entender la operacion. */}
            <div className="col-12 mt-2">
                <div className="text-xs uppercase text-color-secondary mb-2">
                    Lo que podés ver
                    {datos?.verTodo && <span className="ml-2">· todas las alertas</span>}
                    <span className="ml-2 normal-case" style={{ textTransform: 'none' }}>
                        · tocá cualquier barra para ver esas alertas
                    </span>
                </div>
            </div>

            <div className="col-12 lg:col-7">
                <div className="card h-full">
                    <div className="flex justify-content-between align-items-baseline mb-3">
                        <span className="font-bold">Por día</span>
                        <span className="text-sm text-color-secondary">
                            {nro(total)} en total · {nro(promedio)} por día activo
                        </span>
                    </div>
                    {porDia.length === 0 ? (
                        <div className="text-color-secondary">Sin datos en el rango.</div>
                    ) : (
                        <div className="flex align-items-end gap-1" style={{ height: '11rem' }}>
                            {porDia.map((x, i) => {
                                // Una de cada tres etiquetas cuando son 30 barras: en un
                                // celular no entran treinta fechas.
                                const cada = porDia.length > 20 ? 3 : 1;
                                const alto = maxDia ? Math.max(2, Math.round((x.n / maxDia) * 100)) : 2;
                                // La columna necesita height 100%: sin eso el alto en
                                // porcentaje de la barra no tiene contra que resolver y el
                                // grafico sale vacio.
                                const ir = () => irAConsulta({ desde: x.dia, hasta: x.dia });
                                return (
                                    <div key={x.dia}
                                         {...clicable(ir, `${x.dia}: ${nro(x.n)} — ver estas alertas`)}
                                         className="flex-1 flex flex-column justify-content-end
                                                    align-items-center gap-1 barra-clic"
                                         style={{ minWidth: 0, height: '100%', cursor: 'pointer' }}>
                                        <div style={{
                                                 width: '100%', height: `${alto}%`,
                                                 background: 'var(--primary-color)',
                                                 borderRadius: '3px 3px 0 0',
                                                 transition: 'filter .15s ease'
                                             }} />
                                        <div className="text-xs text-color-secondary"
                                             style={{ fontSize: '.65rem' }}>
                                            {i % cada === 0 ? diaCorto(x.dia) : ''}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            <div className="col-12 lg:col-5">
                <div className="card h-full">
                    <div className="font-bold mb-3">Por tipo</div>
                    {porTipo.length === 0 ? (
                        <div className="text-color-secondary">Sin datos en el rango.</div>
                    ) : porTipo.length === 1 ? (
                        // Con un solo tipo, una barra al 100% es una plancha de color
                        // que no informa nada. Se muestra el numero y se dice que es
                        // todo lo que hay.
                        <div {...clicable(() => irAConsulta({ tipo: porTipo[0].tipo }),
                                          'Ver estas alertas')}>
                            <TagTipo tipo={porTipo[0].tipo} />
                            <div className="text-3xl font-bold mt-2">{nro(porTipo[0].n)}</div>
                            <div className="text-sm text-color-secondary">
                                es el único tipo en tu alcance
                            </div>
                            <div className="text-xs mt-2" style={{ color: 'var(--primary-color)' }}>
                                Ver las {nro(porTipo[0].n)} alertas <i className="pi pi-arrow-right"
                                    style={{ fontSize: '.7rem' }} />
                            </div>
                        </div>
                    ) : porTipo.map((x) => {
                        const pct = totalTipo ? Math.round((x.n / totalTipo) * 1000) / 10 : 0;
                        return (
                            <div key={x.tipo} className="mb-3 barra-clic"
                                 {...clicable(() => irAConsulta({ tipo: x.tipo }),
                                              `${x.tipo}: ${nro(x.n)} — ver estas alertas`)}>
                                <div className="flex justify-content-between align-items-center mb-1">
                                    <TagTipo tipo={x.tipo} />
                                    <span className="text-sm text-color-secondary">
                                        {nro(x.n)} · {pct}%
                                    </span>
                                </div>
                                <div style={{
                                    height: 6, borderRadius: 3, background: 'var(--surface-100)',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%', width: `${Math.max(1.5, pct)}%`,
                                        borderRadius: 3,
                                        background: COLOR_TIPO[x.tipo] || 'var(--primary-color)'
                                    }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* El tipo no alcanza: dentro de CANDADO conviven un cable insertado,
                que es rutina, y un corte de cable, que hay que mirar ya. */}
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-baseline mb-3">
                        <span className="font-bold">Por evento</span>
                        <span className="text-sm text-color-secondary">
                            qué pasó exactamente, dentro de cada tipo
                        </span>
                    </div>
                    {porEvento.length === 0 ? (
                        <div className="text-color-secondary">Sin datos en el rango.</div>
                    ) : porEvento.map((x) => {
                        const nivel = nivelEvento(x.evento);
                        const col = COLOR_NIVEL[nivel];
                        return (
                            <div key={x.evento} className="mb-2 barra-clic"
                                 {...clicable(() => irAConsulta({ evento: x.evento }),
                                              `${x.evento}: ${nro(x.n)} — ver estas alertas`)}>
                                <div className="flex justify-content-between align-items-baseline gap-2">
                                    <span className="text-sm inline-flex align-items-center gap-2">
                                        <span style={{
                                            width: 8, height: 8, borderRadius: '50%',
                                            flex: '0 0 8px', background: col
                                        }} />
                                        <span style={nivel === 'critico' ? { fontWeight: 700 } : undefined}>
                                            {x.evento}
                                        </span>
                                    </span>
                                    <span className="text-sm text-color-secondary">{nro(x.n)}</span>
                                </div>
                                <div style={{
                                    height: 6, borderRadius: 3, background: 'var(--surface-100)',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        height: '100%', borderRadius: 3, background: col,
                                        width: `${maxEvento ? Math.max(1.5, (x.n / maxEvento) * 100) : 2}%`
                                    }} />
                                </div>
                            </div>
                        );
                    })}
                    <small className="text-color-secondary">
                        Rojo exige mirar ya · ámbar hay que leerlo · verde es el curso normal.
                    </small>
                </div>
            </div>

            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between align-items-baseline mb-3">
                        <span className="font-bold">Por transportista</span>
                        <span className="text-sm text-color-secondary">
                            los 12 con más alertas
                            {transp?.dias && transp.dias !== dias && ` · últimos ${transp.dias} días`}
                        </span>
                    </div>
                    {cargandoTransp ? (
                        <div className="text-color-secondary">Calculando…</div>
                    ) : porTransp.length === 0 ? (
                        <div className="text-color-secondary">Sin datos en el rango.</div>
                    ) : porTransp.map((x) => (
                        <div key={x.transportista} className="mb-2 barra-clic"
                             {...clicable(
                                 () => irAConsulta(x.transportista === '(sin dato)'
                                     ? {} : { transportista: x.transportista }),
                                 x.transportista === '(sin dato)'
                                     ? 'Sin transportista resuelto: abre la consulta del rango'
                                     : `${x.transportista}: ${nro(x.n)} — ver estas alertas`)}>
                            <div className="flex justify-content-between align-items-baseline">
                                <span className="text-sm">{x.transportista}</span>
                                <span className="text-sm text-color-secondary">{nro(x.n)}</span>
                            </div>
                            <div style={{
                                height: 6, borderRadius: 3, background: 'var(--surface-100)',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    height: '100%', borderRadius: 3,
                                    width: `${maxTransp ? Math.max(1.5, (x.n / maxTransp) * 100) : 2}%`,
                                    background: x.transportista === '(sin dato)'
                                        ? 'var(--surface-400)' : 'var(--primary-color)'
                                }} />
                            </div>
                        </div>
                    ))}
                    <small className="text-color-secondary">
                        Los nombres salen normalizados: el catálogo tiene el mismo transportista
                        cargado dos veces con distinta caja.
                        {transp?.dias && transp.dias < dias &&
                            ` Este corte se limita a ${transp.dias} días: es el único que necesita
                              recorrer la bitácora y con más rango no termina en un tiempo razonable.`}
                    </small>
                </div>
            </div>
        </div>
    );
};

export default TableroAlertas;
